import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "../anim/Reveal";
import Icon from "../icons/Icon";
import Section from "../layout/Section";
import MicroLabel from "../ui/MicroLabel";
import Statement from "../ui/Statement";
import TicketCard from "../ui/TicketCard";
import { SITE_CONTENT } from "../../constants/siteContent";
import styles from "./IndustriesExplorer.module.css";

/* IndustriesExplorer — the sector list, as a vertical tab set on paper.
   <Section tone="paper" size="lg"> owns the surface, the vertical rhythm and
   the container. LEFT is the sector list in the display face; RIGHT is the
   active sector's photo (unframed, panel radius), a ticket caption, its sourced
   copy and the link into that sector.

   No pin, no scroll listener, no rAF: the active sector changes ONLY on click
   or keyboard. That is what makes real tab semantics honest here —
     > 720px   role="tablist" / "tab" / "tabpanel", aria-selected, aria-controls,
               roving tabindex, Arrow / Home / End move focus AND selection.
     <= 720px  the list is replaced by a native <select>, and the panel becomes a
               plain labelled region (a tabpanel with no tablist in the tree
               would misdescribe the widget).
   The swap is done in JS (matchMedia), so exactly ONE control exists in the DOM
   at a time and both write the same state. */

const COMPACT_QUERY = "(max-width: 720px)";

const slugify = (label) =>
  label
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const pad2 = (value) => String(value).padStart(2, "0");

const cx = (...values) => values.filter(Boolean).join(" ");

/* ---------------------------------------------------------------- content --
   Every sentence on screen is READ from SITE_CONTENT at module load — nothing
   is retyped here, so copy can never drift from its source. */
const PAGES = Array.isArray(SITE_CONTENT.pages) ? SITE_CONTENT.pages : [];

const findPage = (path) => PAGES.find((page) => page.path === path);

/* One sector line from the /industries overview page, looked up by heading. */
const overviewLine = (heading) => {
  const page = findPage("/industries");
  const match =
    page && Array.isArray(page.sections)
      ? page.sections.find((section) => section.heading === heading)
      : null;
  return match && match.body ? match.body : "";
};

/* The subtitle of a sector's own page. */
const pageSubtitle = (path) => {
  const page = findPage(path);
  return page && page.subtitle ? page.subtitle : "";
};

/* TODO(content): presentation map, keyed by the exact SITE_CONTENT.industries
   label.
     icon   a name from the shared icon set (an unknown name renders nothing)
     image  an owned asset in public/images/pages — decorative atmosphere only,
            so it renders with alt="" and is greyscaled to sit in the palette
     page   the sector's own route in SITE_CONTENT.pages, if it has one
     body   a live read of SITE_CONTENT (see the source note on each entry)

   A sector whose `body` resolves to "" does NOT render — it is dropped from the
   explorer rather than given invented positioning ("Media & Entertainment" has
   no line anywhere in SITE_CONTENT today). To surface a new sector: add its
   real copy to SITE_CONTENT first, then map it here. Never write the claim in
   this file. */
const SECTORS = {
  Healthcare: {
    icon: "health",
    image: "/images/pages/healthcare.jpg",
    page: "/healthcare-software-solutions",
    // source: pages["/industries"] → "Healthcare and Life Sciences"
    body: overviewLine("Healthcare and Life Sciences")
  },
  "Software & Technology": {
    icon: "software",
    image: "/images/pages/cloud.jpg",
    // source: pages["/industries"] → "Software and Technology"
    body: overviewLine("Software and Technology")
  },
  "Fintech & Banking": {
    icon: "finance",
    image: "/images/pages/fintech.jpg",
    page: "/fintech-banking-solutions",
    // source: pages["/industries"] → "Fintech and Banking"
    body: overviewLine("Fintech and Banking")
  },
  "Insurance & Risk": {
    icon: "insurance",
    image: "/images/pages/devops.jpg",
    // source: pages["/industries"] → "Insurance and Risk"
    body: overviewLine("Insurance and Risk")
  },
  "Real Estate": {
    icon: "realestate",
    image: "/images/pages/web-design-new-1.jpg",
    page: "/real-estate-digital-solutions",
    // source: pages["/real-estate-digital-solutions"].subtitle
    body: pageSubtitle("/real-estate-digital-solutions")
  },
  "Travel & Logistics": {
    icon: "travel",
    image: "/images/pages/travel-logistics.jpg",
    page: "/travel-logistics-solutions",
    // source: caseStudies.subtitle — the live products ARE this sector. (The
    // sector page's own subtitle counts "three of our four" products, which
    // would contradict the work grid on this same homepage.)
    body: (SITE_CONTENT.caseStudies && SITE_CONTENT.caseStudies.subtitle) || ""
  },
  "Public Sector": {
    icon: "publicsector",
    image: "/images/pages/public-sector.jpg",
    page: "/public-sector-it-solutions",
    // source: pages["/industries"] → "Public Sector and Enterprise Operations"
    body: overviewLine("Public Sector and Enterprise Operations")
  },
  "E-Commerce & Retail": {
    icon: "retail",
    image: "/images/pages/web-design-new-5.jpg",
    // source: pages["/industries"] → "Retail, E-Commerce, and Consumer"
    body: overviewLine("Retail, E-Commerce, and Consumer")
  }
};

/* Honesty gate. Order comes from the real SITE_CONTENT.industries list; only a
   sector with sourced copy renders, and a `page` only becomes a link when that
   route really exists in SITE_CONTENT.pages. */
const INDUSTRIES = (SITE_CONTENT.industries || [])
  .filter((label) => Boolean(SECTORS[label] && SECTORS[label].body))
  .map((label) => {
    const { page, ...sector } = SECTORS[label];
    return {
      id: slugify(label),
      label,
      ...sector,
      href: page && findPage(page) ? page : null
    };
  });

/* TODO(content): UI labels. The headline is a re-line of
   SITE_CONTENT.industriesSection.title ("Industries we build for") — same
   meaning, no new claim; `titleAccent` is the word set in the serif. */
const LABELS = {
  eyebrow: "Industries",
  titleLead: "Sectors we",
  titleAccent: "build",
  titleTail: "for.",
  tablist: "Industries",
  select: "Choose an industry",
  explore: "Explore",
  all: "All industries",
  allHref: "/industries"
};

const LEAD = (SITE_CONTENT.industriesSection || {}).subtitle || "";

/* Live media-query flag. Not a scroll listener: it fires only when the viewport
   crosses the breakpoint. */
function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(query).matches
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }
    const list = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);
    setMatches(list.matches);

    if (typeof list.addEventListener === "function") {
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    }
    // Safari < 14 only has the deprecated pair
    list.addListener(onChange);
    return () => list.removeListener(onChange);
  }, [query]);

  return matches;
}

export default function IndustriesExplorer() {
  /* `previous` keeps the outgoing photo on screen UNDER the incoming one, so a
     photo that is still downloading never flashes the empty frame. */
  const [view, setView] = useState({ active: 0, previous: -1 });
  /* index -> "requested" | "ready" | "failed". Only a requested photo is
     mounted, so the page downloads one photo up front instead of all of them. */
  const [photos, setPhotos] = useState({ 0: "requested" });
  const compact = useMediaQuery(COMPACT_QUERY);
  const tabRefs = useRef([]);

  /* Every label lost its sourced copy — render nothing rather than an empty
     shell. After the hooks, so hook order never changes. */
  if (INDUSTRIES.length === 0) {
    return null;
  }

  const total = INDUSTRIES.length;
  const activeIndex = view.active < total ? view.active : 0;
  const active = INDUSTRIES[activeIndex];
  const activeTabId = `ind-tab-${active.id}`;

  const setPhoto = (index, status) =>
    setPhotos((prev) => {
      // never downgrade: a second request must not reset a photo that is ready
      if (prev[index] === status || (status === "requested" && prev[index])) {
        return prev;
      }
      return { ...prev, [index]: status };
    });

  const activate = (index) => {
    if (index < 0 || index >= total) {
      return;
    }
    /* The layer held underneath is the last one that actually SHOWED a photo:
       stepping quickly past a sector whose photo has not arrived yet must not
       swap a loaded picture for an empty layer. */
    setView((prev) =>
      prev.active === index
        ? prev
        : {
            active: index,
            previous:
              photos[prev.active] === "ready" ? prev.active : prev.previous
          }
    );
    setPhoto(index, "requested");
  };

  /* Vertical tablist keys (WAI-ARIA APG, automatic activation). Left / Right
     are honoured too, because the list reflows into two columns on tablets. */
  const onTabKeyDown = (event) => {
    const from = tabRefs.current.indexOf(event.target);
    const current = from === -1 ? activeIndex : from;
    let next;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        next = current === total - 1 ? 0 : current + 1;
        break;
      case "ArrowUp":
      case "ArrowLeft":
        next = current === 0 ? total - 1 : current - 1;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = total - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    activate(next);
    const node = tabRefs.current[next];
    if (node) {
      node.focus();
    }
  };

  const onSelectChange = (event) => {
    activate(INDUSTRIES.findIndex((item) => item.id === event.target.value));
  };

  return (
    <Section
      tone="paper"
      size="lg"
      id="industries"
      aria-labelledby="industries-title"
    >
      <div className={styles.head}>
        <div className={styles.headMain}>
          <MicroLabel as="p" className={styles.eyebrow}>
            {LABELS.eyebrow}
          </MicroLabel>
          {/* an h2: HeadingWipe (App.js) animates it — never wrap it in Reveal */}
          <Statement as="h2" size="h2" id="industries-title">
            {LABELS.titleLead} <em>{LABELS.titleAccent}</em> {LABELS.titleTail}
          </Statement>
        </div>
        {LEAD ? (
          <Reveal as="p" className={styles.lead} delay={80}>
            {LEAD}
          </Reveal>
        ) : null}
      </div>

      <div className={styles.body}>
        {/* LEFT — the sector list (> 720px) or the select (<= 720px) */}
        <Reveal className={styles.nav}>
          {compact ? (
            <div className={styles.selectWrap}>
              <MicroLabel
                as="label"
                htmlFor="ind-select"
                className={styles.selectLabel}
              >
                {LABELS.select}
              </MicroLabel>
              <span className={styles.selectField}>
                <select
                  id="ind-select"
                  className={styles.select}
                  aria-controls="ind-panel"
                  value={active.id}
                  onChange={onSelectChange}
                >
                  {INDUSTRIES.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <Icon
                  name="chevron-down"
                  size={20}
                  className={styles.selectChevron}
                />
              </span>
            </div>
          ) : (
            <div
              className={styles.tabs}
              role="tablist"
              aria-label={LABELS.tablist}
              aria-orientation="vertical"
              style={{ "--ind-rows": Math.ceil(total / 2) }}
              onKeyDown={onTabKeyDown}
            >
              {INDUSTRIES.map((item, index) => {
                const isActive = index === activeIndex;
                /* onMouseEnter starts the photo early, so it is usually there
                   by the time the click lands. It never changes the tab. */
                return (
                  <button
                    key={item.id}
                    ref={(node) => {
                      tabRefs.current[index] = node;
                    }}
                    type="button"
                    role="tab"
                    id={`ind-tab-${item.id}`}
                    className={cx(styles.tab, isActive && styles.tabActive)}
                    aria-selected={isActive}
                    aria-controls="ind-panel"
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => activate(index)}
                    onMouseEnter={() => setPhoto(index, "requested")}
                    data-analytics-interaction={`industry_select:${item.label}`}
                  >
                    <span
                      className={`micro ${styles.tabNum}`}
                      aria-hidden="true"
                    >
                      {pad2(index + 1)}
                    </span>
                    <span className={styles.tabLabel}>{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </Reveal>

        {/* RIGHT — the active sector */}
        <Reveal className={styles.panelCol} delay={120}>
          <div
            id="ind-panel"
            className={styles.panel}
            role={compact ? "region" : "tabpanel"}
            aria-labelledby={compact ? undefined : activeTabId}
            aria-label={compact ? active.label : undefined}
            tabIndex={compact ? undefined : 0}
          >
            {/* Decorative: stock atmosphere + a caption that repeats the tab's
                own label, so the whole block is hidden from assistive tech. */}
            <div className={styles.media} aria-hidden="true">
              <div className={styles.frame}>
                {INDUSTRIES.map((item, index) => {
                  const status = photos[index];
                  return (
                    <div
                      key={item.id}
                      className={cx(
                        styles.layer,
                        index === view.previous && styles.layerPrev,
                        index === activeIndex && styles.layerActive,
                        status === "failed" && styles.layerFailed
                      )}
                    >
                      {status && status !== "failed" ? (
                        <img
                          className={cx(
                            styles.img,
                            status === "ready" && styles.imgReady
                          )}
                          src={process.env.PUBLIC_URL + item.image}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          onLoad={() => setPhoto(index, "ready")}
                          onError={() => setPhoto(index, "failed")}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <TicketCard tone="paper" className={styles.caption}>
                <Icon
                  name={active.icon}
                  size={18}
                  className={styles.captionIcon}
                />
                <MicroLabel className={styles.captionLabel}>
                  {active.label}
                </MicroLabel>
                <MicroLabel className={styles.captionCount}>
                  {`${pad2(activeIndex + 1)} / ${pad2(total)}`}
                </MicroLabel>
              </TicketCard>
            </div>

            <div className={styles.copy}>
              {/* keyed, so the copy re-enters each time the sector changes */}
              <p key={active.id} className={styles.desc}>
                {active.body}
              </p>
              <div className={styles.actions}>
                {active.href ? (
                  <Link className="link-arrow" to={active.href}>
                    {`${LABELS.explore} ${active.label}`}
                  </Link>
                ) : null}
                <Link className="link-arrow" to={LABELS.allHref}>
                  {LABELS.all}
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}
